import { supabase } from '@/shared/lib/supabase';

/**
 * adminReportsService — staff-side report queries.
 *
 * Each method reads either an existing v_admin_* view or runs a focused
 * aggregate query. RLS enforced via is_staff() in policies on the underlying
 * tables; staff JWT passes through.
 */

export type DashboardMetrics = {
  active_customers: number;
  suspended_customers: number;
  churned_customers: number;
  lead_customers: number;
  /** Past due only. Was every open invoice until 2026-08-30. */
  overdue_total: number;
  /** Issued, not yet due. */
  due_total: number;
  /** overdue + due. The honest headline figure. */
  outstanding_total: number;
  invoices_in_grace: number;
  invoices_suspended: number;
  invoices_unpaid: number;
  open_tickets: number;
  failed_jobs: number;
  active_jobs: number;
  mrr: number;
};

export type LifecycleFunnelRow = {
  lifecycle_stage: string;
  customer_count: number;
  mrr_in_stage: number;
};

export type ProvisioningStats = {
  total: number;
  pending: number;
  running: number;
  failed_retryable: number;
  failed_terminal: number;
  complete: number;
  cancelled: number;
  /**
   * Same six buckets over the trailing 30 days. The lifetime success rate is a
   * number that barely moves — 79 jobs since 2026-05-01 means one bad week is
   * diluted into invisibility. Measured 2026-08-31: lifetime 92.4%, last 30 days
   * 86.8%. The page shows both so the trend is legible.
   */
  window_30d: {
    total: number;
    complete: number;
    failed_terminal: number;
    failed_retryable: number;
  };
  by_step_kind: Array<{ step_kind: string; count: number }>;
  recent_failures: Array<{
    id: string;
    step_kind: string;
    target_ref: string | null;
    error_code: string | null;
    error_message: string | null;
    attempt_count: number;
    finished_at: string | null;
  }>;
};

export type TicketSlaStats = {
  total_open: number;
  total_closed_30d: number;
  breached: number;
  approaching: number;
  ok: number;
  avg_first_response_hours: number | null;
  avg_resolution_hours: number | null;
};

export type DomainsReport = {
  total_active_links: number;
  expiring_in_30d: number;
  expired: number;
  auto_renew_enabled: number;
  auto_renew_disabled: number;
  in_flight_orders: number;
};

export type AcquisitionSummaryRow = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  signups: number;
  customers_with_service: number;
  active_customers: number;
  first_signup_at: string | null;
  most_recent_signup_at: string | null;
};

/**
 * Phase 10 reports metrics — Care + Studio + Email Setup snapshot for the
 * Reports Overview tiles. Read-through (AD-27); each query hits live data.
 */
export type Phase10Metrics = {
  // Care Plan
  care_active_subs: number;          // active or at_risk
  care_at_risk_subs: number;         // status = 'at_risk' only
  care_mrr_zar: number;              // current month MRR from view
  care_mrr_history: Array<{ month_start: string; mrr_zar: number }>;
  // Studio Starter
  studio_orders_lifetime: number;    // count of progressed orders (paid+)
  studio_orders_this_month: number;  // service_order.created_at >= this month
  studio_in_progress: number;        // status IN (awaiting_consultation, in_progress)
  // Cross — Care attach rate (Studio customers who also have Care)
  care_attach_rate: number;          // 0..1 ratio
  care_attach_studio_count: number;  // denominator
  care_attach_overlap: number;       // numerator
};

export type ServiceOrderRow = {
  id: string;
  sku_code: string;
  status: string;
  customer_id: string;
  customer_email: string | null;
  primary_fqdn: string | null;
  total_paid_cents: number;
  created_at: string;
  delivered_at: string | null;
  complete_at: string | null;
  slo_credit_issued_at: string | null;
};

export type MissedSlaRow = {
  service_order_id: string;
  customer_id: string;
  customer_email: string | null;
  primary_fqdn: string | null;
  brief_completed_at: string;
  delivered_at: string | null;
  slo_credit_issued_at: string;
};

export type ServicesReportData = {
  // KPIs
  total_service_orders: number;
  studio_orders_lifetime: number;
  outstanding_balance_at_risk_zar: number;
  missed_sla_count: number;
  // Detail
  orders: ServiceOrderRow[];
  missed_sla_log: MissedSlaRow[];
};

export const adminReportsService = {
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const { data, error } = await supabase
      .from('v_admin_dashboard_metrics')
      .select('*')
      .single();
    if (error) throw new Error(`Could not load dashboard metrics: ${error.message}`);
    return data as DashboardMetrics;
  },

  async getLifecycleFunnel(): Promise<LifecycleFunnelRow[]> {
    const { data, error } = await supabase
      .from('v_admin_lifecycle_funnel')
      .select('lifecycle_stage, customer_count, mrr_in_stage')
      .order('customer_count', { ascending: false });
    if (error) throw new Error(`Could not load lifecycle funnel: ${error.message}`);
    return (data ?? []) as LifecycleFunnelRow[];
  },

  async getProvisioningStats(): Promise<ProvisioningStats> {
    // Aggregate by status
    const { data: byStatus, error: byStatusErr } = await supabase
      .from('provisioning_job')
      .select('status, created_at');
    if (byStatusErr) throw new Error(`Could not load provisioning stats: ${byStatusErr.message}`);

    const counts: Record<string, number> = {};
    const win = { total: 0, complete: 0, failed_terminal: 0, failed_retryable: 0 };
    const windowStart = Date.now() - 30 * 86_400_000;
    for (const r of (byStatus ?? []) as Array<{ status: string; created_at: string }>) {
      counts[r.status] = (counts[r.status] ?? 0) + 1;
      if (new Date(r.created_at).getTime() >= windowStart) {
        win.total += 1;
        if (r.status === 'complete') win.complete += 1;
        if (r.status === 'failed_terminal') win.failed_terminal += 1;
        if (r.status === 'failed_retryable') win.failed_retryable += 1;
      }
    }

    // Aggregate by step_kind
    const { data: byKind } = await supabase.from('provisioning_job').select('step_kind');
    const kindCounts = new Map<string, number>();
    for (const r of (byKind ?? []) as Array<{ step_kind: string }>) {
      kindCounts.set(r.step_kind, (kindCounts.get(r.step_kind) ?? 0) + 1);
    }
    const by_step_kind = Array.from(kindCounts.entries())
      .map(([step_kind, count]) => ({ step_kind, count }))
      .sort((a, b) => b.count - a.count);

    // The 20 most recent failures — of ALL time, not "last 7d" as this comment
    // used to claim. There is no date predicate below, and adding one would
    // hide the standing `failed_terminal` backlog that nothing retries.
    const { data: failures } = await supabase
      .from('provisioning_job')
      .select('id, step_kind, target_ref, error_code, error_message, attempt_count, finished_at')
      .in('status', ['failed_terminal', 'failed_retryable'])
      .order('finished_at', { ascending: false, nullsFirst: false })
      .limit(20);

    return {
      total: byStatus?.length ?? 0,
      pending: counts.pending ?? 0,
      running: counts.running ?? 0,
      failed_retryable: counts.failed_retryable ?? 0,
      failed_terminal: counts.failed_terminal ?? 0,
      complete: counts.complete ?? 0,
      cancelled: counts.cancelled ?? 0,
      window_30d: win,
      by_step_kind,
      recent_failures: (failures ?? []) as ProvisioningStats['recent_failures'],
    };
  },

  async getTicketSlaStats(): Promise<TicketSlaStats> {
    const { data: tickets, error } = await supabase
      .from('ticket')
      .select('id, status, created_at, first_response_at, resolved_at, department_id, department:department_id(sla_hours)');
    if (error) throw new Error(`Could not load ticket stats: ${error.message}`);

    const rows = (tickets ?? []) as Array<{
      status: string;
      created_at: string;
      first_response_at: string | null;
      resolved_at: string | null;
      department: { sla_hours: number } | { sla_hours: number }[] | null;
    }>;
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 86_400_000;

    let total_open = 0;
    let total_closed_30d = 0;
    let breached = 0;
    let approaching = 0;
    let ok = 0;
    const firstResponses: number[] = [];
    const resolutions: number[] = [];

    for (const t of rows) {
      const sla = Array.isArray(t.department) ? t.department[0]?.sla_hours : t.department?.sla_hours;
      const slaMs = (sla ?? 24) * 3_600_000;
      const created = new Date(t.created_at).getTime();

      if (t.status !== 'closed') {
        total_open++;
        if (!t.first_response_at) {
          if (now - created > slaMs) breached++;
          else if (now - created > slaMs * 0.75) approaching++;
          else ok++;
        } else {
          ok++;
          firstResponses.push(new Date(t.first_response_at).getTime() - created);
        }
      } else {
        if (t.resolved_at && new Date(t.resolved_at).getTime() > thirtyDaysAgo) {
          total_closed_30d++;
          if (t.first_response_at) {
            firstResponses.push(new Date(t.first_response_at).getTime() - created);
          }
          resolutions.push(new Date(t.resolved_at).getTime() - created);
        }
      }
    }

    const avg = (arr: number[]) =>
      arr.length === 0 ? null : Math.round(arr.reduce((s, v) => s + v, 0) / arr.length / 3_600_000 * 10) / 10;

    return {
      total_open,
      total_closed_30d,
      breached,
      approaching,
      ok,
      avg_first_response_hours: avg(firstResponses),
      avg_resolution_hours: avg(resolutions),
    };
  },

  async getDomainsReport(): Promise<DomainsReport> {
    const { data: links, error } = await supabase
      .from('domain_link')
      .select('id, expiry_date, auto_renew, unlinked_at')
      .is('unlinked_at', null);
    if (error) throw new Error(`Could not load domains report: ${error.message}`);

    const today = new Date();
    let expiring = 0;
    let expired = 0;
    let autoOn = 0;
    let autoOff = 0;
    for (const l of (links ?? []) as Array<{ expiry_date: string | null; auto_renew: boolean }>) {
      if (l.auto_renew) autoOn++;
      else autoOff++;
      if (!l.expiry_date) continue;
      const exp = new Date(l.expiry_date + 'T00:00:00Z').getTime();
      const days = Math.round((exp - today.getTime()) / 86_400_000);
      if (days < 0) expired++;
      else if (days <= 30) expiring++;
    }

    const { count: orderCount } = await supabase
      .from('domain_registration_order')
      .select('id', { count: 'exact', head: true })
      .not('status', 'in', '(complete,cancelled,failed)');

    return {
      total_active_links: links?.length ?? 0,
      expiring_in_30d: expiring,
      expired,
      auto_renew_enabled: autoOn,
      auto_renew_disabled: autoOff,
      in_flight_orders: orderCount ?? 0,
    };
  },

  async getAcquisitionSummary(): Promise<AcquisitionSummaryRow[]> {
    const { data, error } = await supabase
      .from('v_admin_acquisition_summary')
      .select('*');
    if (error) throw new Error(`Could not load acquisition summary: ${error.message}`);
    return (data ?? []) as AcquisitionSummaryRow[];
  },

  /**
   * Phase 10 metrics for the Reports Overview tiles (T-10.1-13 + T-10.2-13).
   * 5 parallel queries, all read-through:
   *   - v_admin_care_mrr_monthly  (12-month series; latest = current MRR)
   *   - care_subscription         (at_risk count)
   *   - count_studio_starter_orders RPC (lifetime progressed orders)
   *   - service_order             (this-month studio + in-progress count)
   */
  async getPhase10Metrics(): Promise<Phase10Metrics> {
    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);
    const monthStartIso = monthStart.toISOString();

    const [
      careHistory,
      careAtRisk,
      studioLifetime,
      studioThisMonth,
      studioInProgress,
      studioCustomers,
      careActiveUsers,
    ] = await Promise.all([
      supabase
        .from('v_admin_care_mrr_monthly')
        .select('month_start, mrr_zar, active_subscriptions')
        .order('month_start', { ascending: true }),
      supabase
        .from('care_subscription')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'at_risk'),
      supabase.rpc('count_studio_starter_orders'),
      supabase
        .from('service_order')
        .select('id', { count: 'exact', head: true })
        .eq('sku_code', 'studio-starter')
        .gte('created_at', monthStartIso),
      supabase
        .from('service_order')
        .select('id', { count: 'exact', head: true })
        .eq('sku_code', 'studio-starter')
        .in('status', ['awaiting_consultation', 'in_progress']),
      // Care attach rate: distinct customer_ids that hold a non-cancelled
      // Studio order (denominator) + the subset who also hold an active or
      // at_risk Care subscription (numerator).
      supabase
        .from('service_order')
        .select('customer_id')
        .eq('sku_code', 'studio-starter')
        .not('status', 'in', '(cancelled,failed_terminal,pending_payment)'),
      supabase
        .from('care_subscription')
        .select('user_id')
        .in('status', ['active', 'at_risk']),
    ]);

    if (careHistory.error) throw new Error(`Could not load Care MRR history: ${careHistory.error.message}`);

    type HistRow = { month_start: string; mrr_zar: number | string; active_subscriptions: number | string };
    const rows = (careHistory.data ?? []) as HistRow[];
    const history = rows.map((r) => ({
      month_start: r.month_start,
      mrr_zar: typeof r.mrr_zar === 'string' ? parseFloat(r.mrr_zar) : r.mrr_zar,
    }));
    const latest = rows[rows.length - 1];
    const careActiveSubs = latest
      ? typeof latest.active_subscriptions === 'string'
        ? parseInt(latest.active_subscriptions, 10)
        : latest.active_subscriptions
      : 0;
    const careMrrZar = latest
      ? typeof latest.mrr_zar === 'string'
        ? parseFloat(latest.mrr_zar)
        : latest.mrr_zar
      : 0;

    // Care attach rate
    const studioCustomerSet = new Set(
      ((studioCustomers.data ?? []) as Array<{ customer_id: string }>).map((r) => r.customer_id),
    );
    const careUserSet = new Set(
      ((careActiveUsers.data ?? []) as Array<{ user_id: string }>).map((r) => r.user_id),
    );
    let overlap = 0;
    studioCustomerSet.forEach((id) => {
      if (careUserSet.has(id)) overlap++;
    });
    const attachRate = studioCustomerSet.size > 0 ? overlap / studioCustomerSet.size : 0;

    return {
      care_active_subs: careActiveSubs,
      care_at_risk_subs: careAtRisk.count ?? 0,
      care_mrr_zar: careMrrZar,
      care_mrr_history: history,
      studio_orders_lifetime: (studioLifetime.data as number | null) ?? 0,
      studio_orders_this_month: studioThisMonth.count ?? 0,
      studio_in_progress: studioInProgress.count ?? 0,
      care_attach_rate: attachRate,
      care_attach_studio_count: studioCustomerSet.size,
      care_attach_overlap: overlap,
    };
  },

  /**
   * Services deep-dive report (T-10.3-07 + T-10.3-08).
   * Returns the four KPIs + the full service-order list + the missed-SLA log
   * for the Services report page. Read-through; client filters/sorts.
   */
  async getServicesReport(): Promise<ServicesReportData> {
    const [orders, missedSla, atRiskInstallments] = await Promise.all([
      supabase
        .from('service_order')
        .select(
          'id, sku_code, status, customer_id, primary_fqdn, total_paid_cents, created_at, delivered_at, complete_at, slo_credit_issued_at',
        )
        .order('created_at', { ascending: false }),
      supabase
        .from('service_order')
        .select(
          'id, customer_id, primary_fqdn, brief_completed_at, delivered_at, slo_credit_issued_at',
        )
        .not('slo_credit_issued_at', 'is', null)
        .order('slo_credit_issued_at', { ascending: false }),
      supabase
        .from('installment_schedule')
        .select('service_order:service_order_id(total_paid_cents)')
        .not('last_failure_at', 'is', null)
        .lt('escalation_step', 6),
    ]);

    if (orders.error) throw new Error(`Could not load service orders: ${orders.error.message}`);

    // The query selects all ServiceOrderRow fields except customer_email
    // (which we resolve below from customer_profile in a batched lookup).
    type RawOrder = Omit<ServiceOrderRow, 'customer_email'>;
    const orderRows = (orders.data ?? []) as RawOrder[];
    const slaRows = (missedSla.data ?? []) as Array<{
      id: string;
      customer_id: string;
      primary_fqdn: string | null;
      brief_completed_at: string;
      delivered_at: string | null;
      slo_credit_issued_at: string;
    }>;

    // Resolve customer emails in one batched query
    const userIds = Array.from(
      new Set([...orderRows.map((r) => r.customer_id), ...slaRows.map((r) => r.customer_id)]),
    );
    const emailMap = new Map<string, string>();
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('customer_profile')
        .select('id, billing_email')
        .in('id', userIds);
      ((profiles ?? []) as Array<{ id: string; billing_email: string | null }>).forEach((p) => {
        if (p.billing_email) emailMap.set(p.id, p.billing_email);
      });
    }

    // Outstanding balance at risk — sum of (399000 - total_paid_cents) over
    // all at-risk Studio installments (joined service_order row).
    type AtRiskRow = {
      service_order:
        | { total_paid_cents: number }
        | Array<{ total_paid_cents: number }>
        | null;
    };
    const atRiskRows = (atRiskInstallments.data ?? []) as AtRiskRow[];
    const outstandingCents = atRiskRows.reduce((acc, r) => {
      const so = Array.isArray(r.service_order) ? r.service_order[0] : r.service_order;
      const paid = so?.total_paid_cents ?? 0;
      return acc + Math.max(0, 399000 - paid);
    }, 0);

    const ordersWithEmail: ServiceOrderRow[] = orderRows.map((r) => ({
      ...r,
      customer_email: emailMap.get(r.customer_id) ?? null,
    }));
    const missedLog: MissedSlaRow[] = slaRows.map((r) => ({
      service_order_id: r.id,
      customer_id: r.customer_id,
      customer_email: emailMap.get(r.customer_id) ?? null,
      primary_fqdn: r.primary_fqdn,
      brief_completed_at: r.brief_completed_at,
      delivered_at: r.delivered_at,
      slo_credit_issued_at: r.slo_credit_issued_at,
    }));

    const studioLifetime = ordersWithEmail.filter(
      (o) =>
        o.sku_code === 'studio-starter' &&
        !['cancelled', 'failed_terminal', 'pending_payment'].includes(o.status),
    ).length;

    return {
      total_service_orders: ordersWithEmail.length,
      studio_orders_lifetime: studioLifetime,
      outstanding_balance_at_risk_zar: outstandingCents / 100,
      missed_sla_count: missedLog.length,
      orders: ordersWithEmail,
      missed_sla_log: missedLog,
    };
  },
};
